import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import Keycloak from 'keycloak-js';
import { LoggerService } from '../services/logger.service';
import { environment } from '../../../environments/environment';

const CONTEXT = 'ErrorInterceptor';

/**
 * Global HTTP error interceptor.
 *
 * - 0:   Network error / CORS failure — logs error
 * - 401: Redirects to Keycloak login for admin endpoints (session expired)
 * - 403: Logs access denied
 * - 5xx: Logs server errors
 *
 * Public endpoints (containing /public/) are NOT redirected to login on 401,
 * since they use token-based auth, not Keycloak sessions.
 *
 * All errors are re-thrown so callers can still handle them individually.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(Keycloak);
  const logger = inject(LoggerService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isPublicEndpoint = req.url.includes('/public/');

      switch (error.status) {
        case 0:
          logger.error(`Server unreachable: ${req.method} ${req.url}`, CONTEXT);
          break;
        case 401:
          if (!isPublicEndpoint) {
            logger.warn('Session expired — redirecting to login', CONTEXT);
            void keycloak.login({ idpHint: environment.keycloak.idpHint });
          }
          break;
        case 403:
          logger.warn(`Access denied: ${req.method} ${req.url}`, CONTEXT);
          break;
        default:
          if (error.status >= 500) {
            logger.error(`Server error ${error.status}: ${req.method} ${req.url}`, CONTEXT);
          }
          break;
      }

      return throwError(() => error);
    }),
  );
};
